"use client";

import type { CaptionStyle } from "@teksta/shared-types";
import { ChevronLeft, ChevronRight, Redo2, Scissors, Trash2, Undo2 } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { cn } from "@/lib/utils";
import type { AudioWaveformConfig } from "../_components/audioWaveform";
import { CaptionOverlay } from "../_components/CaptionOverlay";
import { captionMovePatch, captionResizeFontSizeRel } from "../_components/captionBoxDrag";
import { type CaptionPreviewSpec, measureCaptionBox } from "../_components/captions";
import { AUDIO_RENDER_SIZE, useVideoContentRect } from "../_components/videoRect";

const clamp = (value: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, value));

interface DragState {
  mode: "move" | "resize";
  /** move: pointer offset from the box's top-left at grab. */
  grabDx: number;
  grabDy: number;
  /** resize: scale the font by the pointer's distance from this fixed anchor. */
  anchorX: number;
  anchorY: number;
  startDist: number;
  startFont: number;
}

/**
 * Compact action button for the on-stage caption editor's button row. Also
 * used by the composition editor's stage caption editor.
 */
export function StageActionButton({
  label,
  title,
  icon,
  iconRight,
  destructive,
  disabled,
  onClick,
}: {
  /** Visible text; omit for an icon-only button (set `title` for its name). */
  label?: string;
  /** Accessible name / tooltip (defaults to `label`). */
  title?: string;
  icon?: ReactNode;
  /** Icon rendered after the label (e.g. a trailing direction arrow). */
  iconRight?: ReactNode;
  destructive?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={title ?? label}
      aria-label={title ?? label}
      className={cn(
        "flex items-center gap-0.5 rounded border px-1.5 py-0.5 hover:bg-foreground/10 disabled:opacity-40 disabled:hover:bg-transparent",
        destructive && "text-destructive hover:bg-destructive/10"
      )}
    >
      {icon}
      {label}
      {iconRight}
    </button>
  );
}

/**
 * The editor preview stage: the source `<video>` (no native controls — the
 * editor owns playback) with the live caption overlay on top, and a draggable
 * selection box around the caption when one is selected and on screen.
 *
 * Direct manipulation (per the chosen model: align + vertical offset, not free
 * x/y): drag the box to move it — vertically it sets position/offset (snapping
 * to top/center/bottom zones), horizontally it snaps alignment to left/center/
 * right. The corner handle resizes the font. A whole gesture is one undo step
 * (drag key + `endGroup` on release). The box is measured to the real rendered
 * text via `measureCaptionBox`, so it matches the caption exactly.
 */
export function EditorStage({
  videoRef,
  previewUrl,
  isAudio,
  spec,
  audioWaveform,
  audioBackgroundImageDataUrl,
  audioMixActive,
  activeText,
  activeCue,
  playing,
  cueCount,
  selected,
  onSelect,
  onDeselect,
  onStyle,
  onEditCueText,
  onSplitCue,
  onSplitCueAtCursor,
  onMergeCue,
  onDeleteCue,
  onStepCue,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  endGroup,
  onLoadedDuration,
}: {
  videoRef: RefObject<HTMLVideoElement | HTMLAudioElement | null>;
  previewUrl: string;
  /** Audio-only media: no video frame, so the stage renders a default 16:9 frame. */
  isAudio: boolean;
  spec: CaptionPreviewSpec | null;
  /** Optional audio-only waveform visual render settings. */
  audioWaveform?: AudioWaveformConfig | null;
  /** Optional base64 background for audio-only visual renders. */
  audioBackgroundImageDataUrl?: string | null;
  /** The base media's audio should stay silent because stem playback is active. */
  audioMixActive?: boolean;
  /** Text of the cue on screen at the playhead, or null when none. */
  activeText: string | null;
  /** The cue on screen at the playhead (index + text), or null when none. */
  activeCue: { index: number; text: string } | null;
  playing: boolean;
  cueCount: number;
  selected: boolean;
  onSelect: () => void;
  onDeselect: () => void;
  onStyle: (patch: Partial<CaptionStyle>, key?: string | null) => void;
  onEditCueText: (index: number, text: string) => void;
  /** Split cue `index` at the playhead (used by the right-click menu). */
  onSplitCue: (index: number) => void;
  /** Split cue `index` at a text caret offset (used by the editor's Split button). */
  onSplitCueAtCursor: (index: number, charOffset: number) => void;
  /** Merge cue `index` with the one after it. */
  onMergeCue: (index: number) => void;
  onDeleteCue: (index: number) => void;
  /** Move the on-screen caption to the previous/next cue (and seek there). */
  onStepCue: (delta: -1 | 1) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  endGroup: () => void;
  onLoadedDuration: (seconds: number) => void;
}) {
  const t = useTranslations("mediaTools.editor");
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRect = useVideoContentRect(videoRef, isAudio ? AUDIO_RENDER_SIZE : undefined);
  const drag = useRef<DragState | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Intrinsic media aspect ratio (width/height). It drives the stage box so the
  // preview scales to the video's shape and a stable height, instead of the
  // `<video>` stretching to the full column width (huge on ultrawide screens).
  // Audio has no picture, and before metadata loads we don't know it yet, so
  // both fall back to 16:9.
  const [videoAspect, setVideoAspect] = useState<number | null>(null);
  const aspectRatio = isAudio ? 16 / 9 : (videoAspect ?? 16 / 9);

  // Re-measure the selection box when a webfont finishes loading — its metrics
  // change once the real font replaces the fallback.
  const [, setFontTick] = useState(0);
  useEffect(() => {
    const bump = () => setFontTick((n) => n + 1);
    document.fonts?.addEventListener("loadingdone", bump);
    return () => document.fonts?.removeEventListener("loadingdone", bump);
  }, []);

  // Track the editor's caret position so "Split" can disable at the text edges.
  // `selectionchange` fires on every caret move in the focused field.
  const [caret, setCaret] = useState<number | null>(null);
  useEffect(() => {
    if (!selected) {
      setCaret(null);
      return;
    }
    const onSelectionChange = () => {
      const el = textareaRef.current;
      if (el && document.activeElement === el) setCaret(el.selectionStart);
    };
    onSelectionChange();
    document.addEventListener("selectionchange", onSelectionChange);
    return () => document.removeEventListener("selectionchange", onSelectionChange);
  }, [selected]);

  // Click-outside-to-deselect. Subscribed only while selected so it doesn't
  // re-bind on every playback frame; onDeselect is read via a ref.
  const onDeselectRef = useRef(onDeselect);
  onDeselectRef.current = onDeselect;
  useEffect(() => {
    if (!selected) return;
    const onDocPointerDown = (event: PointerEvent) => {
      const node = event.target as Node | null;
      if (node && !containerRef.current?.contains(node)) onDeselectRef.current();
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [selected]);

  // Exact caption bounds, in container coordinates — computed whenever paused with
  // a caption on screen, so the caption is a click target (to enter edit mode)
  // even before it's selected. Hidden during playback.
  // Measure at the intrinsic media resolution — the same basis the export and the
  // caption overlay lay out at — then scale the box down to display coords, so the
  // selection box hugs the rendered caption (it wraps identically only at a shared
  // resolution; see CaptionOverlay / ContentRect).
  const measured =
    !playing && activeText && spec && contentRect
      ? measureCaptionBox(spec.style, activeText, contentRect.sourceWidth, contentRect.sourceHeight)
      : null;
  const displayScale = contentRect ? contentRect.width / contentRect.sourceWidth : 1;
  const box =
    measured && contentRect
      ? {
          left: contentRect.left + measured.left * displayScale,
          top: contentRect.top + measured.top * displayScale,
          width: measured.width * displayScale,
          height: measured.height * displayScale,
        }
      : null;

  const toLocal = (clientX: number, clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (!box || event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = toLocal(event.clientX, event.clientY);
    if (!point) return;
    drag.current = {
      mode: "move",
      grabDx: point.x - box.left,
      grabDy: point.y - box.top,
      anchorX: 0,
      anchorY: 0,
      startDist: 1,
      startFont: 0,
    };
  };

  const startResize = (event: ReactPointerEvent<HTMLElement>) => {
    if (!spec || !box || event.button !== 0) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = toLocal(event.clientX, event.clientY);
    if (!point) return;
    // Anchor at the box's top-left; font scales with the pointer's distance from
    // it, so dragging the corner out grows the caption and dragging in shrinks it.
    drag.current = {
      mode: "resize",
      grabDx: 0,
      grabDy: 0,
      anchorX: box.left,
      anchorY: box.top,
      startDist: Math.hypot(point.x - box.left, point.y - box.top) || 1,
      startFont: spec.style.fontSizeRel,
    };
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const state = drag.current;
    if (!state || !contentRect || !box || !spec) return;

    if (state.mode === "resize") {
      const point = toLocal(event.clientX, event.clientY);
      if (!point) return;
      const dist = Math.hypot(point.x - state.anchorX, point.y - state.anchorY);
      onStyle(
        { fontSizeRel: captionResizeFontSizeRel(state.startFont, state.startDist, dist) },
        "drag-resize"
      );
      return;
    }

    const point = toLocal(event.clientX, event.clientY);
    if (!point) return;
    onStyle(
      captionMovePatch(spec.style, contentRect, box, point, {
        dx: state.grabDx,
        dy: state.grabDy,
      }),
      "drag-move"
    );
  };

  const endDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (!drag.current) return;
    drag.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    endGroup();
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={containerRef}
          // Aspect-ratio box: width fills the column but is capped so the height
          // never exceeds 60vh (maxWidth = 60vh × ratio), then centred. Because
          // the box matches the media ratio, `object-contain` adds no black bars.
          className="relative mx-auto w-full overflow-hidden rounded-lg bg-black"
          style={{ aspectRatio, maxWidth: `calc(60vh * ${aspectRatio})` }}
          onPointerDown={(event) => {
            // Clicking the video background (not the caption) closes edit mode.
            if (event.button === 0 && selected) onDeselect();
          }}
        >
          <video
            ref={videoRef as RefObject<HTMLVideoElement>}
            src={previewUrl}
            // Load remote (Teksta) media in CORS mode so a later cross-origin
            // export fetch of the same URL reuses the cache instead of failing
            // ("Failed to fetch"). Ignored for local blob: URLs.
            crossOrigin={previewUrl.startsWith("blob:") ? undefined : "anonymous"}
            playsInline
            // Fills the aspect-ratio container above; that box owns the sizing so
            // audio-only (no picture) still gets a 16:9 frame for captions.
            className="h-full w-full bg-black object-contain"
            onLoadedMetadata={(event) => {
              const el = event.currentTarget;
              const total = el.duration;
              if (Number.isFinite(total) && total > 0) onLoadedDuration(total);
              if (el.videoWidth > 0 && el.videoHeight > 0) {
                setVideoAspect(el.videoWidth / el.videoHeight);
              }
            }}
          />
          {spec && (
            <CaptionOverlay
              videoRef={videoRef}
              spec={spec}
              sourceSize={isAudio ? AUDIO_RENDER_SIZE : undefined}
              audioWaveformUrl={isAudio ? previewUrl : null}
              audioWaveform={isAudio ? (audioWaveform ?? null) : null}
              audioBackgroundImageDataUrl={isAudio ? audioBackgroundImageDataUrl : null}
              audioWaveformOutputMuted={audioMixActive}
            />
          )}

          {/* Not selected, paused: the caption is a click target — click to edit. */}
          {box && !selected && (
            <button
              type="button"
              aria-label={t("actions.editCaption")}
              className="absolute cursor-pointer rounded-sm ring-1 ring-primary/0 transition hover:ring-2 hover:ring-primary/60"
              style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={onSelect}
            />
          )}

          {/* Selected: move/resize box + the inline text editor with prev/next. */}
          {box && selected && (
            <div
              className="absolute cursor-move touch-none rounded-sm ring-2 ring-primary"
              style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
              onPointerDown={startMove}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
            >
              <div
                className="-right-1.5 -bottom-1.5 absolute h-3 w-3 cursor-nwse-resize touch-none rounded-full border border-background bg-primary"
                onPointerDown={startResize}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
              />
            </div>
          )}
          {box && selected && activeCue && (
            <div
              className="absolute z-30 w-72"
              style={{
                // Anchor to the caption's horizontal centre (stable for centre-aligned
                // captions as the text grows while typing), clamped to the video area.
                left: clamp(
                  box.left + box.width / 2 - 144,
                  4,
                  (contentRect?.left ?? 0) + (contentRect?.width ?? 0) - 292
                ),
                top: box.top > 96 ? box.top - 8 : box.top + box.height + 8,
                transform: box.top > 96 ? "translateY(-100%)" : undefined,
              }}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <div className="space-y-1.5 rounded-md border bg-background p-1.5 shadow-md">
                <textarea
                  ref={textareaRef}
                  autoFocus
                  rows={2}
                  // Controlled: reflects the doc, so prev/next and merge/split update
                  // it automatically. Live commit per keystroke (coalesced into one
                  // undo step by the parent's per-cue key); preview updates as you type.
                  value={activeCue.text}
                  aria-label={t("actions.editCaptionText")}
                  className="w-full resize-none rounded border bg-background p-1.5 text-xs outline-hidden"
                  onChange={(event) => onEditCueText(activeCue.index, event.currentTarget.value)}
                  onBlur={endGroup}
                  onKeyDown={(event) => {
                    if (event.key === "Escape" || (event.key === "Enter" && !event.shiftKey)) {
                      event.preventDefault();
                      endGroup();
                      onDeselect();
                    }
                  }}
                />
                <div className="flex items-center justify-between text-foreground/60 text-xs">
                  <button
                    type="button"
                    disabled={activeCue.index <= 0}
                    onClick={() => onStepCue(-1)}
                    className="flex items-center gap-0.5 rounded px-1 py-0.5 hover:bg-foreground/10 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" /> {t("actions.prev")}
                  </button>
                  <span className="tabular-nums">
                    {activeCue.index + 1} / {cueCount}
                  </span>
                  <button
                    type="button"
                    disabled={activeCue.index >= cueCount - 1}
                    onClick={() => onStepCue(1)}
                    className="flex items-center gap-0.5 rounded px-1 py-0.5 hover:bg-foreground/10 disabled:opacity-40"
                  >
                    {t("actions.next")} <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-1 border-t pt-1.5 text-xs">
                  <StageActionButton
                    title={t("actions.undo")}
                    icon={<Undo2 className="h-3.5 w-3.5" />}
                    disabled={!canUndo}
                    onClick={onUndo}
                  />
                  <StageActionButton
                    title={t("actions.redo")}
                    icon={<Redo2 className="h-3.5 w-3.5" />}
                    disabled={!canRedo}
                    onClick={onRedo}
                  />
                  <span className="mx-0.5 h-4 w-px bg-foreground/15" />
                  <StageActionButton
                    label={t("actions.merge")}
                    title={t("actions.mergeWithPrevious")}
                    icon={<ChevronLeft className="h-3.5 w-3.5" />}
                    disabled={activeCue.index <= 0}
                    onClick={() => onMergeCue(activeCue.index - 1)}
                  />
                  <StageActionButton
                    label={t("actions.split")}
                    title={t("actions.splitAtCursor")}
                    icon={<Scissors className="h-3 w-3" />}
                    // Off unless the caret sits between two non-empty sides.
                    disabled={caret === null || caret <= 0 || caret >= activeCue.text.length}
                    onClick={() =>
                      onSplitCueAtCursor(
                        activeCue.index,
                        textareaRef.current?.selectionStart ?? activeCue.text.length
                      )
                    }
                  />
                  <StageActionButton
                    label={t("actions.merge")}
                    title={t("actions.mergeWithNext")}
                    iconRight={<ChevronRight className="h-3.5 w-3.5" />}
                    disabled={activeCue.index >= cueCount - 1}
                    onClick={() => onMergeCue(activeCue.index)}
                  />
                  <span className="mx-0.5 h-4 w-px bg-foreground/15" />
                  <StageActionButton
                    label={t("actions.delete")}
                    icon={<Trash2 className="h-3 w-3" />}
                    destructive
                    onClick={() => {
                      onDeleteCue(activeCue.index);
                      onDeselect();
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        {activeCue && !playing && (
          <>
            <ContextMenuItem onSelect={onSelect}>{t("actions.editText")}</ContextMenuItem>
            <ContextMenuItem onSelect={() => onSplitCue(activeCue.index)}>
              {t("actions.splitAtPlayhead")}
            </ContextMenuItem>
            <ContextMenuItem
              disabled={activeCue.index >= cueCount - 1}
              onSelect={() => onMergeCue(activeCue.index)}
            >
              {t("actions.mergeWithNext")}
            </ContextMenuItem>
            <ContextMenuItem
              disabled={activeCue.index <= 0}
              onSelect={() => onMergeCue(activeCue.index - 1)}
            >
              {t("actions.mergeWithPrevious")}
            </ContextMenuItem>
            <ContextMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={() => onDeleteCue(activeCue.index)}
            >
              {t("actions.delete")}
            </ContextMenuItem>
            <ContextMenuSeparator />
          </>
        )}
        <ContextMenuItem
          onSelect={() => onStyle({ position: "bottom", offsetRel: 0.08, align: "center" })}
        >
          {t("stage.resetCaptionPosition")}
        </ContextMenuItem>
        <ContextMenuItem onSelect={() => onStyle({ position: "center", align: "center" })}>
          {t("stage.centerCaption")}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
